import { useInfiniteQuery } from 'react-query';
import { ReviewWorkflowsResponse, ReviewWorkflowsListParams } from '../_types';
import {
  performanceReportingApiUrl,
  fetcher,
  handleError,
  ServerErrorResponse,
  createParamString,
} from 'data_sources/constants';

export const getReviewWorkflows = async (
  params: ReviewWorkflowsListParams & { nextToken?: string | null } = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<ReviewWorkflowsResponse> => {
  try {
    const url = `${performanceReportingApiUrl()}/performance/review-workflow/v1/review-workflows`;

    const queryParams = createParamString({
      nextToken: params.nextToken,
      organizationId: params.organizationId,
    });
    const fullUrl = queryParams ? `${url}?${queryParams}` : url;

    const response = await fetcher({
      url: fullUrl,
      method: 'GET',
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useReviewWorkflows = (
  params: ReviewWorkflowsListParams = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void
) => {
  return useInfiniteQuery<ReviewWorkflowsResponse, ServerErrorResponse>(
    ['review-workflows', JSON.stringify(params)],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return await getReviewWorkflows(combinedParams, signal, onError);
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
      ...options,
    }
  );
};

export default useReviewWorkflows;
